import { useEffect, type RefObject } from 'react';
import type { ActiveMatch } from '@/components/cash-flow/CashFlowWorkspace';

/** The transient highlight class a located row/chip pulses (defined in index.css). */
export const LOCATE_FLASH_CLASS = 'pg-locate-flash';

/**
 * Reveal the document a "locate" request points at, scoped to `containerRef`. When
 * `activeMatch` changes the hook finds the descendant tagged `data-entry="<id>"`,
 * scrolls it to the centre and re-fires a one-shot flash. Shared by EVERY cash-flow
 * lens (calendar · matrix · register) so locate behaves identically across them
 * (replit.md L14: one function, every lens) — pure DOM, holds no money or state.
 */
export function useLocateScroll(
  containerRef: RefObject<HTMLElement | null>,
  activeMatch: ActiveMatch | null,
): void {
  useEffect(() => {
    const root = containerRef.current;
    if (!activeMatch || !root) return;
    const selector = `[data-entry="${activeMatch.entryId.replace(/["\\]/g, '\\$&')}"]`;
    const el = root.querySelector<HTMLElement>(selector);
    if (!el) return;
    el.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' });
    // Restart the CSS flash even when the same element is located again (force reflow).
    el.classList.remove(LOCATE_FLASH_CLASS);
    void el.offsetWidth;
    el.classList.add(LOCATE_FLASH_CLASS);
    return () => el.classList.remove(LOCATE_FLASH_CLASS);
  }, [containerRef, activeMatch]);
}
